// The backend host/protocol are resolved in ONE place: src/config/apiConfig.js.
// Switch cloud vs local (and paste a new server IP) there, not here.
import apiConfig from '../config/apiConfig';

export default class UrlHelper {
  proto = apiConfig.proto;
  ip = apiConfig.host;
  base = '';

  constructor() {
    // In development all calls go through the CRA proxy at /api (see
    // setupProxy.js, which reads the SAME apiConfig). In a production build
    // they hit the resolved origin directly.
    this.base = process.env.NODE_ENV === 'development'
      ? '/api'
      : apiConfig.origin;
  }

  // --- CSV / upload ---
  csvBulkSubmitUrl() {
    return `${this.base}/UI/frontend/csvBulkSubmit`;
  }
  // Re-Issue Membership Passes. Multipart body is `issuer_id` + `image` —
  // note the club field is named issuer_id here, NOT the `clubid` that
  // csvBulkSubmitUrl uses. The CSV carries only: number, mobile number.
  reIssuePassUrl() {
    return `${this.base}/UI/frontend/reissue-pass`;
  }
  // Job status for ANY of the three bulk imports (bulk passes, re-issue passes,
  // bulk coupons). Each POST answers 202 with { error, message, jobid } and does
  // the work in the background; this is polled until the job reports done.
  //   → { error, message, job: { totalCount, failed, success, status,
  //                              errorFile, uploadedCsv } }
  //   status: 0 = processing, 1 = finished, 2 = severe backend error.
  jobPollStatusUrl(jobId) {
    return `${this.base}/UI/frontend/get-poll-status?jobid=${encodeURIComponent(jobId)}`;
  }
  // Jobs currently RUNNING for one club, so a page can pick a job back up after
  // the user navigated away. Shared by all three bulk pages — each picks out its
  // own `process` code (see JOB_PROCESS in useJobPoller.js).
  //   200 -> { error: 0, jobs: [{ id, process, ...counters }] }
  //   207 -> { error: 1, message: 'No active jobs available', jobs: [] }
  // The 207 is a NORMAL state, not a failure — read `jobs`, not `error`.
  // Only active jobs are returned, so a run that finished while the user was
  // away does not appear here.
  activeJobsUrl(issuerId) {
    return `${this.base}/UI/frontend/get-active-jobs?iId=${encodeURIComponent(issuerId)}`;
  }
  // Re-Issue a single pass. JSON body (not multipart):
  //   { issuer_id, input_type: 'member_id',   memberId, mobile }
  //   { issuer_id, input_type: 'card_number', cardId,   mobile }
  // 200 => { error: 0, message }, 207 => { error: 1, message }.
  reIssueSinglePassUrl() {
    return `${this.base}/UI/frontend/reissue-single-pass`;
  }
  downloadCsvUrl(fileName) {
    return `${this.base}/UI/frontend/csvdownload?file=${fileName}`;
  }
  validateCsvUrl() {
    return `${this.base}/UI/frontend/validatecsv`;
  }

  // --- auth ---
  validateUserUrl() {
    return `${this.base}/UI/frontend/validateuser`;
  }

  // --- reports ---
  consumerReportUrl() {
    return `${this.base}/UI/report/consumer`;
  }
  scanReportUrl() {
    return `${this.base}/UI/report/scan`;
  }

  // --- Summary Report / success metrics (POST, JSON body) ---
  // Body: { issuerId, startDate, endDate } (+ offerId for efficiency; + offerId
  // & result for closed-loop). "All" selections send integer 0
  // (issuerId/offerId/result). See SUMMARY_REPORT_PLAN.md §9. Paths pending
  // server-team confirmation.
  walletPassAdoptionUrl() {
    return `${this.base}/UI/report/metrics/wallet-pass-adoption`;
  }
  activePassUsageUrl() {
    return `${this.base}/UI/report/metrics/active-pass-usage`;
  }
  offerRedemptionReachUrl() {
    return `${this.base}/UI/report/metrics/offer-redemption-reach`;
  }
  offerRedemptionEfficiencyUrl() {
    return `${this.base}/UI/report/metrics/offer-redemption-efficiency`;
  }
  closedLoopEvidenceUrl() {
    return `${this.base}/UI/report/metrics/closed-loop-evidence`;
  }
  reportDownloadUrl(file) {
    return `${this.base}/UI/report/metrics/download?file=${encodeURIComponent(file)}`;
  }

  // --- General Report / scan volume over time (POST, JSON body) ---
  // Body: { issuerId, startDate, endDate }. All three return the SAME envelope:
  // { error, message, startDate, endDate, series: [{ key, label, total,
  // points: [{ date, count }] }] } so the client needs one parser.
  // scans-by-passtype additionally carries a `type` per row, which the client
  // folds into the three pass-type buckets. See GENERAL_REPORT_PLAN.md §10.
  generalScansByPassTypeUrl() {
    return `${this.base}/UI/report/general/scans-by-passtype`;
  }
  generalScansByLocationUrl() {
    return `${this.base}/UI/report/general/scans-by-location`;
  }
  generalScansByPromotionUrl() {
    return `${this.base}/UI/report/general/scans-by-promotion`;
  }

  // --- S7000 ---
  // POST (add) and PUT (update) both use the base path; server keys the
  // single config by issuer in the body. DELETE/GET use ?iId= (s7000Url_GET).
  s7000Url() {
    return `${this.base}/UI/frontend/s7000`;
  }
  s7000Url_GET(clubId) {
    return `${this.base}/UI/frontend/s7000?iId=${clubId}`;
  }

  // --- shelly lock ---
  shellyLockUrl_GET(lId) {
    return `${this.base}/UI/frontend/shelly?lId=${lId}`;
  }
  shellylockUrl() {
    return `${this.base}/UI/frontend/shelly`;
  }
  shellylockUrlById(id) {
    return `${this.base}/UI/frontend/shelly?id=${id}`;
  }
  // Delete is keyed by the location id (one shelly lock per location), not the
  // shelly id and not an issuer id.
  shellylockUrl_DELETE(lId) {
    return `${this.base}/UI/frontend/shelly?lId=${lId}`;
  }

  // --- member pass ---
  memberpassUrl() {
    return `${this.base}/UI/frontend/memberpass`;
  }
  unknownmemberpassUrl() {
    return `${this.base}/UI/frontend/unknownmemberpass`;
  }
  // Single-coupon: returns the promotions valid for a given member (member id or
  // mobile). POST { issuer_id, member_input_type, member_value }.
  scpUrl() {
    return `${this.base}/UI/frontend/scp`;
  }
  promotionMemberUrl() {
    return `${this.base}/UI/frontend/promotionmember`;
  }

  // --- promotion <-> location map ---
  promotionLocationUrl() {
    return `${this.base}/UI/frontend/promotionlocationmap`;
  }
  promotionLocationUrlByPromo(promoId, issuerId) {
    return `${this.base}/UI/frontend/promotionlocationmap?promoid=${promoId}&iId=${issuerId}`;
  }

  // --- printer make ---
  printerMakeUrl() {
    return `${this.base}/UI/frontend/pmake`;
  }
  printerMakeUrlById(id) {
    return `${this.base}/UI/frontend/pmake?id=${id}`;
  }

  // --- printer / scanning device location ---
  printerlocationUrl_GET(lId, iId) {
    return `${this.base}/UI/frontend/locationprinters?lId=${lId}&iId=${iId}`;
  }
  printerlocationUrl() {
    return `${this.base}/UI/frontend/locationprinters`;
  }
  printerlocationUrlById(id) {
    return `${this.base}/UI/frontend/locationprinters?id=${id}`;
  }
  deviceTypesUrl() {
    return `${this.base}/UI/frontend/devicetypes`;
  }

  // --- locations ---
  locationUrl() {
    return `${this.base}/UI/frontend/locations`;
  }
  locationUrlById(id) {
    return `${this.base}/UI/frontend/locations?id=${id}`;
  }
  locationUrl_GET(rId) {
    return `${this.base}/UI/frontend/locations?iId=${rId}`;
  }

  // --- redemption partners ---
  rpartnerUrl() {
    return `${this.base}/UI/frontend/RedemptionPartners`;
  }
  rpartnerUrlById(id) {
    return `${this.base}/UI/frontend/RedemptionPartners?pId=${id}`;
  }
  rpartnerUrl_GET(iId) {
    return `${this.base}/UI/frontend/RedemptionPartners?iId=${iId}`;
  }

  // --- partners / clubs ---
  partnerUrl() {
    return `${this.base}/UI/frontend/partners`;
  }
  partnerUrlById(id) {
    return `${this.base}/UI/frontend/partners?id=${id}`;
  }
  // A single club/partner scoped by its issuer id (used to read its expiry date).
  partnerUrlByIssuer(iId) {
    return `${this.base}/UI/frontend/partners?iId=${iId}`;
  }

  // --- brands ---
  brandUrl() {
    return `${this.base}/UI/frontend/brands`;
  }
  brandUrlById(id) {
    return `${this.base}/UI/frontend/brands?id=${id}`;
  }
  brandUrl_GET(partnerId) {
    return `${this.base}/UI/frontend/brands?iId=${partnerId}`;
  }

  // --- promotions ---
  promotionUrl() {
    return `${this.base}/UI/frontend/promotion`;
  }
  promotionUrlById(id) {
    return `${this.base}/UI/frontend/promotion?id=${id}`;
  }
  promotionUrl_GET(issuerId, activeFlag) {
    return `${this.base}/UI/frontend/promotion?iId=${issuerId}&active=${activeFlag}`;
  }
  // All promotions for an issuer (no active filter -> server returns all non-expired)
  promotionUrlAllByIssuer(issuerId) {
    return `${this.base}/UI/frontend/promotion?iId=${issuerId}`;
  }
  // status: 0=all, 1=active+future, 2=active, 3=future, 4=expired.
  promotionUrlByStatus(issuerId, status) {
    return `${this.base}/UI/frontend/promotion?iId=${issuerId}&status=${status}`;
  }
  promotionUrl_GetWithIssuerId(brandId, issuerId) {
    return `${this.base}/UI/frontend/promotion?bId=${brandId}&iId=${issuerId}`;
  }

  // --- misc ---
  omniqponUrl() {
    return `${this.base}/UI/frontend/oqimage`;
  }

  // --- users ---
  userUrl() {
    return `${this.base}/UI/frontend/users`;
  }
  userUrlById(id) {
    return `${this.base}/UI/frontend/users?id=${id}`;
  }
  // Server deletes users by username (?u=)
  userUrlByUsername(username) {
    return `${this.base}/UI/frontend/users?u=${encodeURIComponent(username)}`;
  }
  userUrl_GET(iId) {
    return `${this.base}/UI/frontend/users?iId=${iId}`;
  }
}
